import React, { useMemo } from 'react'
import {
  type LucideIcon,
  CreditCard,
  Box,
  UserRound,
  ShoppingCart,
  ArrowLeftCircle,
  Terminal,
  Building,
  Inbox,
  FileText,
  Users,
  DollarSign,
  Settings,
  Link,
  PackageSearch,
  Layers,
  Shield,
  Lock
} from "lucide-react"
import { cn } from "@shared/lib/utils"
import { Badge } from "@shared/components/ui/badge"

// Component styles organized by variant and element
const styles = {
  hero: {
    container: "flex flex-1 px-20 py-6 flex-col gap-3 rounded-3xl bg-card shadow-[4px_8px_24px_0_rgba(11,59,69,0.08),0_1px_2px_0_rgba(11,59,69,0.06)] w-full max-w-none",
    grid: "grid grid-cols-1 lg:grid-cols-2 gap-8 items-center h-full min-h-[400px]",
    content: "flex flex-col gap-6 justify-center",
    badge: "bg-primary-light text-foreground text-sm font-semibold px-3 py-1 hover:bg-primary-light",
    title: "text-4xl font-semibold text-foreground leading-tight",
    description: "text-lg text-foreground opacity-70 leading-relaxed font-normal",
    actions: "flex gap-3 items-center",
    imageContainer: "flex justify-center items-center",
    imageWrapper: "relative w-full max-w-lg",
    image: "w-full h-auto object-contain"
  },
  default: {
    container: "border border-dashed border-sidebar-primary rounded-3xl p-8 max-w-lg mx-auto",
    content: "flex flex-col gap-6 items-center text-center w-full",
    iconContainer: "bg-card border border-surface rounded-xl p-2 size-12 flex items-center justify-center shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]",
    icon: "size-6 text-sidebar-primary",
    textWrapper: "flex flex-col gap-2 w-full",
    title: "text-[22px] font-medium text-foreground leading-[100%] tracking-[-0.33px]",
    description: "text-xs text-muted-foreground leading-4 tracking-[-0.12px] opacity-70",
    actions: "flex gap-3 items-center justify-center"
  },
  actionButton: {
    base: "h-12 px-4 py-1 rounded-full flex justify-center items-center text-base font-normal",
    primary: "bg-sidebar-primary hover:bg-sidebar-primary/90 text-sidebar-primary-foreground gap-1.5 shadow-lg",
    secondary: "border border-gray-border bg-card hover:bg-card/90 text-foreground gap-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]",
    icon: "size-4"
  }
} as const

// Internal ActionButton component to eliminate duplication
interface ActionButtonProps {
  action: EmptyStateAction
  index: number
}

const ActionButton = React.memo(({ action, index }: ActionButtonProps) => {
  const isPrimary = action.variant === "primary" || index === 0
  const ActionIcon = action.icon

  if (action.dropdown) {
    return (
      <React.Fragment key={action.label}>
        {action.dropdown}
      </React.Fragment>
    )
  }

  return (
    <button
      key={action.label}
      onClick={action.onClick}
      className={cn(
        styles.actionButton.base,
        isPrimary ? styles.actionButton.primary : styles.actionButton.secondary
      )}
      aria-label={action.label}
    >
      {ActionIcon && !isPrimary && <ActionIcon className={styles.actionButton.icon} aria-hidden="true" />}
      {action.label}
    </button>
  )
})

ActionButton.displayName = 'ActionButton'

export interface EmptyStateAction {
  label: string
  onClick?: () => void
  variant?: "primary" | "secondary"
  icon?: LucideIcon
  dropdown?: React.ReactNode
}

export interface EmptyStateProps {
  title: string
  description: string
  icon?: LucideIcon
  actions?: EmptyStateAction[]
  className?: string
  variant?: "default" | "hero"
  // Hero variant specific props
  badgeText?: string
  imageSrc?: string
  imageAlt?: string
}

export const EmptyState = ({
  title,
  description,
  icon: Icon,
  actions = [],
  className,
  variant = "default",
  badgeText = "Set up in 1 min",
  imageSrc = "/images/pay.png",
  imageAlt = "Payment interface illustration"
}: EmptyStateProps) => {
  // Memoize expensive computations
  const hasActions = useMemo(() => actions.length > 0, [actions.length])

  const actionButtons = useMemo(() =>
    actions.map((action, index) => (
      <ActionButton key={action.label} action={action} index={index} />
    )),
    [actions]
  )
  if (variant === "hero") {
    return (
      <section
        className={cn(styles.hero.container, className)}
        role="region"
        aria-labelledby="hero-title"
      >
        <div className={styles.hero.grid}>
          {/* Content Section */}
          <div className={styles.hero.content}>
            {/* Badge */}
            <div className="inline-flex">
              <Badge
                variant="secondary"
                className={styles.hero.badge}
              >
                {badgeText}
              </Badge>
            </div>

            {/* Heading */}
            <div className="flex flex-col gap-6">
              <h1
                id="hero-title"
                className={styles.hero.title}
              >
                {title}
              </h1>
              <p className={styles.hero.description}>
                {description}
              </p>
            </div>

            {/* Action Buttons */}
            {hasActions && (
              <div
                className={styles.hero.actions}
                role="group"
                aria-label="Available actions"
              >
                {actionButtons}
              </div>
            )}
          </div>

          {/* Illustration Section */}
          <div className={styles.hero.imageContainer}>
            <div className={styles.hero.imageWrapper}>
              <img
                src={imageSrc}
                alt={imageAlt}
                className={styles.hero.image}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      className={cn(styles.default.container, className)}
      role="region"
      aria-labelledby="default-title"
    >
      <div className={styles.default.content}>
        {Icon && (
          <div
            className={styles.default.iconContainer}
            aria-hidden="true"
          >
            <Icon className={styles.default.icon} />
          </div>
        )}

        <div className={styles.default.textWrapper}>
          <h3
            id="default-title"
            className={styles.default.title}
          >
            {title}
          </h3>
          <p className={styles.default.description}>
            {description}
          </p>
        </div>

        {hasActions && (
          <div
            className={styles.default.actions}
            role="group"
            aria-label="Available actions"
          >
            {actionButtons}
          </div>
        )}
      </div>
    </section>
  )
}

export type EntityType =
  | "payment"
  | "product"
  | "customer"
  | "order"
  | "invoice"
  | "quote"
  | "refund"
  | "terminal"
  | "merchant"
  | "member"
  | "dispute"
  | "settlement"
  | "digital"
  | "ecommerce"
  | "payment-link"
  | "inventory"
  | "program"
  | "billing-schedule"
  | "plan"
  | "subscription"
  | "usage-based-billing"
  | "usage-events"
  | "usage-meters"
  | "usage-credits"
  | "velocity"
  | "application"
  | "bank"
  | "organization"
  | "card-program"
  | "program-product"
  | "mm-account"
  | "mm-reserve"
  | "mm-operation"
  | "mm-payout"
  | "mm-adjustment"
  | "mm-deposit"
  | "mm-collected-fee"
  | "tariff"
  | "fee"
  | "fx-rate"
  | "billing-reserve"
  | "billing-payout"
  | "rail"
  | "clearing"
  | "rules-engine"
  | "network-token"
  | "rail-alert"
  | "campaign"
  | "enrollment"
  | "point-transaction"
  | "pos-recipe"
  | "pos-menu"
  | "pos-inventory"
  | "reward"
  | "authorization"
  | "reconciliation"
  | "review-item"
  | "dispute-rail"
  | "fx-schedule"
  | "native-token"
  | "uprdr-batch"
  | "clearing-batch"
  | "card-settlement-transaction"
  | "pricing-table"
  | "revenue-recognition-plan"
  | "team-member"
  | "security-event"
  | "security-policy"
  | "default"

type EmptyStateConfigMap = {
  [key in EntityType]: Omit<EmptyStateProps, "className" | "actions"> & {
    usesHeroVariant?: boolean
  }
}

const emptyStateConfigs: EmptyStateConfigMap = {
  payment: {
    title: "Start accepting payments",
    description: "Begin processing transactions and managing your payment flow seamlessly.",
    icon: CreditCard
  },

  product: {
    title: "Start creating your product catalogue",
    description: "Launch pricing models with built in support for dunning, coupons, and free trials.",
    icon: Box
  },

  customer: {
    title: "Add your first customer",
    description: "Build your customer base and manage relationships effectively.",
    icon: UserRound
  },

  order: {
    title: "No orders yet",
    description: "Start creating orders to manage your sales and fulfillment.",
    icon: ShoppingCart
  },

  invoice: {
    title: "Start automating sales and quotes.",
    description: "You haven't created any invoices yet. Go ahead and create your first one.",
    usesHeroVariant: true
  },

  quote: {
    title: "Start automating sales and quotes.",
    description: "You haven't created any quotes yet. Go ahead and create your first one.",
    usesHeroVariant: true
  },

  refund: {
    title: "No refunds processed",
    description: "Refunds will appear here when you process them for your customers.",
    icon: ArrowLeftCircle
  },

  terminal: {
    title: "Connect your first terminal",
    description: "Set up payment terminals to accept in-person transactions.",
    icon: Terminal
  },

  merchant: {
    title: "Add your first merchant",
    description: "Onboard merchants to start processing payments on their behalf.",
    icon: Building
  },

  member: {
    title: "No members added",
    description: "Add team members to collaborate and manage your organization.",
    icon: Users
  },

  dispute: {
    title: "No disputes found",
    description: "Disputes and chargebacks will appear here when they occur.",
    icon: FileText
  },

  settlement: {
    title: "No settlements yet",
    description: "Settlement reports will appear here as funds are transferred.",
    icon: DollarSign
  },

  digital: {
    title: "No digital products",
    description: "Add digital products to start selling downloadable content.",
    icon: Settings
  },

  ecommerce: {
    title: "Connect your store",
    description: "Integrate your e-commerce platform to sync orders and products.",
    icon: ShoppingCart
  },

  "payment-link": {
    title: "Create payment links",
    description: "Generate shareable payment links for quick transactions.",
    icon: Link
  },

  inventory: {
    title: "Track your inventory",
    description: "Manage stock levels and track product availability.",
    icon: PackageSearch
  },

  program: {
    title: "Create your first program",
    description: "Set up loyalty programs and rewards for your customers.",
    icon: Layers
  },

  'billing-schedule': {
    title: "Start collecting payments when it works for you—per milestone, phase, or plan.",
    description: "Set up payments around your workflow, not a calendar.",
    usesHeroVariant: true
  },

  'plan': {
    title: "Start collecting revenue via automatic recurring billing.",
    description: "Launch pricing models with built in support for dunning, coupons, and free trials.",
    usesHeroVariant: true
  },

  'subscription': {
    title: "Start collecting revenue via automatic recurring billing.",
    description: "Launch pricing models with built in support for dunning, coupons, and free trials.",
    usesHeroVariant: true
  },

  'pricing-table': {
    title: "Embed pricing tables on your website.",
    description: "Let customers choose and subscribe to a plan directly on your site — no backend code required.",
    usesHeroVariant: true
  },

  'usage-based-billing': {
    title: "No usage data yet",
    description: "Usage events, meters, and credits will appear here as your customers use your services.",
    icon: Settings
  },

  'usage-events': {
    title: "Track customer activity",
    description: "Events capture how customers interact with your product. Use them to understand usage patterns and trigger billing.",
    usesHeroVariant: true
  },

  'usage-meters': {
    title: "Ready to boost sales? Measure product usage",
    description: "Meters track customer usage data for billing. Attach them to prices to bill for product or service consumption.",
    usesHeroVariant: true
  },

  'usage-credits': {
    title: "Automatically handle billing credits",
    description: "Allow customers to prepay or use promotional credits for usage-based products or services.",
    usesHeroVariant: true
  },

  velocity: {
  title: "No velocity schedules",
  description: "Create velocity schedules to monitor and enforce transaction limits for your merchants.",
  icon: Inbox
  },
  application: {
  title: "No applications",
  description: "Merchant applications will appear here once submitted.",
  icon: FileText
  },
  bank: {
  title: "No banks",
  description: "Bank members will appear here once onboarded.",
  icon: Building
  },
  organization: {
  title: "No organizations",
  description: "Organizations will appear here once created.",
  icon: Users
  },
  "card-program": {
  title: "No card programs",
  description: "Card programs will appear here once created.",
  icon: Inbox
  },
  "program-product": {
  title: "No program products",
  description: "Products will appear here once added to a program.",
  icon: Inbox
  },
  "mm-account": {
  title: "No accounts",
  description: "Accounts will appear here once created.",
  icon: Inbox
  },
  "mm-reserve": {
  title: "No reserves",
  description: "Reserves will appear here once configured.",
  icon: Inbox
  },
  "mm-operation": {
  title: "No operations",
  description: "Operations will appear here once initiated.",
  icon: Inbox
  },
  "mm-payout": {
  title: "No payouts",
  description: "Payouts will appear here once scheduled.",
  icon: Inbox
  },
  "mm-adjustment": {
  title: "No adjustments",
  description: "Adjustments will appear here once created.",
  icon: Inbox
  },
  "mm-deposit": {
  title: "No deposits",
  description: "Merchant deposits will appear here once initiated.",
  icon: Inbox
  },
  "mm-collected-fee": {
  title: "No collected fees",
  description: "Fee collections will appear here once processed.",
  icon: Inbox
  },
  tariff: {
  title: "No tariffs",
  description: "Tariff plans will appear here once created.",
  icon: Inbox
  },
  fee: {
  title: "No fees",
  description: "Fee configurations will appear here once defined.",
  icon: Inbox
  },
  "fx-rate": {
  title: "No FX rates",
  description: "Foreign exchange rates will appear here once configured.",
  icon: Inbox
  },
  "billing-reserve": {
  title: "No reserves",
  description: "Billing reserves will appear here once set up.",
  icon: Inbox
  },
  "billing-payout": {
  title: "No payout schedules",
  description: "Payout schedules will appear here once created.",
  icon: Inbox
  },
  rail: {
  title: "No rails",
  description: "Payment rails will appear here once connected.",
  icon: Inbox
  },
  clearing: {
  title: "No clearing entries",
  description: "Clearing entries will appear here once transactions are submitted.",
  icon: Inbox
  },
  "rules-engine": {
  title: "No rule sets yet",
  description: "Create your first rule set to start automating decisions with conditional logic.",
  icon: Settings
  },
  "network-token": {
  title: "No network tokens yet",
  description: "Convert cards in your vault to secure network tokens and transact with all your processors.",
  icon: CreditCard
  },
  "rail-alert": {
  title: "No alerts",
  description: "Rail alerts will appear here when they are triggered.",
  icon: Inbox
  },
  authorization: {
  title: "No authorizations",
  description: "Authorization records will appear here once transactions are processed.",
  icon: Inbox
  },
  reconciliation: {
  title: "No reconciliation entries",
  description: "Reconciliation entries will appear here once records are matched.",
  icon: Inbox
  },
  "review-item": {
  title: "No items to review",
  description: "Review items will appear here when they require attention.",
  icon: Inbox
  },
  "dispute-rail": {
  title: "No rail disputes",
  description: "Rail disputes will appear here once they are submitted.",
  icon: Inbox
  },
  "fx-schedule": {
  title: "No FX schedules",
  description: "FX schedule configurations will appear here once created.",
  icon: Inbox
  },
  "native-token": {
  title: "No native tokens",
  description: "Native token records will appear here once created.",
  icon: Inbox
  },
  "uprdr-batch": {
  title: "No UPRDR batches",
  description: "UPRDR batch records will appear here once processed.",
  icon: Inbox
  },
  "clearing-batch": {
  title: "No clearing batches",
  description: "Clearing batch records will appear here once submitted.",
  icon: Inbox
  },
  "card-settlement-transaction": {
  title: "No card settlement transactions",
  description: "Card settlement transaction records will appear here once processed.",
  icon: Inbox
  },
  "revenue-recognition-plan": {
  title: "No revenue recognition plans",
  description: "Create recognition plans to define how revenue is recognized for different transaction types.",
  icon: Inbox
  },
  "team-member": {
    title: "No team members found",
    description: "Invite team members to start collaborating.",
    icon: Users
  },
  "security-event": {
    title: "No security events",
    description: "Security events will appear here as activity occurs.",
    icon: Shield
  },
  "security-policy": {
    title: "No security policies",
    description: "Create a policy to enforce authentication requirements.",
    icon: Lock
  },
  campaign: {
    title: "No campaigns yet",
    description: "Create a campaign to engage your customers with promotions and offers.",
    icon: Inbox
  },
  enrollment: {
    title: "No enrollments yet",
    description: "Enrollments will appear here when customers join your programs.",
    icon: Inbox
  },
  "point-transaction": {
    title: "No point transactions",
    description: "Point transactions will appear here as loyalty activity occurs.",
    icon: Inbox
  },
  "pos-recipe": {
    title: "No recipes yet",
    description: "Create recipes to manage your menu item preparation and costing.",
    icon: Inbox
  },
  "pos-menu": {
    title: "No menus yet",
    description: "Create menus to organize your point-of-sale offerings.",
    icon: Inbox
  },
  "pos-inventory": {
    title: "No inventory items",
    description: "Add inventory items to track your stock levels and costs.",
    icon: Inbox
  },
  reward: {
    title: "No rewards yet",
    description: "Create rewards to incentivize customer engagement and loyalty.",
    icon: Inbox
  },
  default: {
  title: "No results found",
  description: "Try adjusting your filters or search criteria to find what you're looking for.",
  icon: Inbox
  }
  }

// eslint-disable-next-line react-refresh/only-export-components
export function isHeroEmptyStateEntity(entityType: EntityType): boolean {
  const config = emptyStateConfigs[entityType]
  return config?.usesHeroVariant ?? false
}

// eslint-disable-next-line react-refresh/only-export-components
export function getEmptyStateConfig(
  entityType: EntityType,
  customActions?: EmptyStateProps["actions"]
): Omit<EmptyStateProps, "className"> {
  const config = emptyStateConfigs[entityType] || emptyStateConfigs.default

  return {
    ...config,
    actions: customActions || [],
    variant: config.usesHeroVariant ? 'hero' : 'default'
  }
}
